import { useMemo } from 'react';
import { VegaChart, type ChartSpec } from 'src/components/charts';
import type { AssuranceLevel } from 'src/features/reporting/utils/color';

interface AssuranceGaugeProps {
  /** Gauge spec from the server, already coloured and labelled. */
  spec: ChartSpec;
  level: AssuranceLevel | null;
  /** Pixel width. Height follows the spec's own proportions. */
  size?: number;
  ariaLabel?: string;
}

/** The server draws the gauge on a 100-wide frame. The page only sets the pixel size. */
function sizedGauge(spec: ChartSpec, size: number): ChartSpec {
  const frameWidth = typeof spec.width === 'number' ? spec.width : 100;
  const frameHeight = typeof spec.height === 'number' ? spec.height : 56;
  return { ...spec, width: size, height: Math.round(size * (frameHeight / frameWidth)) };
}

/** Hover name of the segment the needle is on, as the server wrote it. */
function activeLabel(spec: ChartSpec): string | undefined {
  const layers = spec.layer;
  if (!Array.isArray(layers)) return undefined;
  const arc = layers[0] as { data?: { values?: Array<{ active?: boolean; label?: unknown }> } };
  const active = arc.data?.values?.find((row) => row.active);
  return typeof active?.label === 'string' ? active.label : undefined;
}

/**
 * Semicircular assurance gauge.
 *
 * Draws the spec the server sent. The dial, the colours, and the hover names
 * are already in it.
 */
export function AssuranceGauge({ spec, level, size = 120, ariaLabel = 'Assurance level' }: AssuranceGaugeProps) {
  const spoken = level === null || level === 'NaN' ? 'no data' : (activeLabel(spec) ?? level);
  const description = `${ariaLabel}: ${spoken}`;
  const sized = useMemo(() => sizedGauge(spec, size), [spec, size]);

  return (
    <div className="inline-flex items-center justify-center" style={{ width: size, height: sized.height as number }}>
      <VegaChart spec={sized} ariaLabel={description} />
    </div>
  );
}
